import { PatientDemographicsUpdateInputSchema } from "@emr/contracts";
import { useLayoutEffect, useState } from "react";
import type {
  PatientDemographics,
  PatientDemographicsUpdateInput,
} from "../../api/clinicalWorkspace";
import { FieldInput } from "./FieldInput";
import { type EditorIssue, SectionEditorFrame } from "./SectionEditorFrame";
import type { FieldSpec } from "./useRowDrafts";

/**
 * Identity, contact, address, emergency contact and coverage in one form.
 * Only the fields that changed travel to `api.update_patient_demographics`
 * (with the patient row version), so an untouched coverage never gets
 * re-issued and the receipt's `changedFields` stays honest.
 *
 * A provincial plan also shows the health card's version code and expiry date
 * beside the number. Each travels with the coverage whenever the card has or
 * had one, so a new number keeps them on screen and an emptied box clears
 * one; a card-only change keeps the plan on file. An imported expiry that is
 * not a full date cannot show in the date box and is left alone until a date
 * is typed.
 */

type FormState = Readonly<Record<string, string>>;

const IDENTITY_SPECS: readonly FieldSpec[] = [
  { key: "legalFirstName", label: "Legal first name", kind: "text", required: true },
  { key: "legalMiddleName", label: "Legal middle name", kind: "text" },
  { key: "legalLastName", label: "Legal last name", kind: "text", required: true },
  { key: "preferredName", label: "Preferred name", kind: "text" },
  { key: "dateOfBirth", label: "Date of birth", kind: "date", required: true, narrow: true },
  {
    key: "administrativeSex",
    label: "Sex",
    kind: "select",
    narrow: true,
    options: [
      { value: "female", label: "Female" },
      { value: "male", label: "Male" },
      { value: "other", label: "Other" },
      { value: "unknown", label: "Not recorded" },
    ],
  },
];

const CONTACT_SPECS: readonly FieldSpec[] = [
  { key: "phone", label: "Phone", kind: "text" },
  { key: "email", label: "Email", kind: "text" },
];

const ADDRESS_SPECS: readonly FieldSpec[] = [
  { key: "address.line1", label: "Address line 1", kind: "text", wide: true },
  { key: "address.line2", label: "Address line 2", kind: "text", wide: true },
  { key: "address.city", label: "City", kind: "text" },
  { key: "address.region", label: "Province / state", kind: "text", narrow: true },
  { key: "address.postalCode", label: "Postal code", kind: "text", narrow: true },
  { key: "address.country", label: "Country", kind: "text", narrow: true },
];

const EMERGENCY_SPECS: readonly FieldSpec[] = [
  { key: "emergencyContact.name", label: "Contact name", kind: "text" },
  { key: "emergencyContact.relationship", label: "Relationship", kind: "text", narrow: true },
  { key: "emergencyContact.phone", label: "Contact phone", kind: "text", narrow: true },
];

const COVERAGE_KIND_SPEC: FieldSpec = {
  key: "coverage.kind",
  label: "Coverage",
  kind: "select",
  narrow: true,
  options: [
    { value: "cash", label: "Cash / none" },
    { value: "insurance", label: "Insured" },
  ],
};

const COVERAGE_SPECS: readonly FieldSpec[] = [
  { key: "coverage.payerName", label: "Plan or insurer", kind: "text", required: true, wide: true },
  {
    key: "coverage.payerKind",
    label: "Plan type",
    kind: "select",
    narrow: true,
    options: [
      { value: "provincial_plan", label: "Provincial health plan" },
      { value: "federal_program", label: "Federal program" },
      { value: "private_insurance", label: "Private insurance" },
      { value: "other", label: "Other" },
    ],
  },
  {
    key: "coverage.memberId",
    label: "Health number / member ID",
    kind: "text",
    required: true,
    narrow: true,
  },
  {
    key: "coverage.versionCode",
    label: "Version code",
    kind: "text",
    narrow: true,
    maxLength: 2,
    placeholder: "AB",
  },
  { key: "coverage.expiryDate", label: "Card expiry date", kind: "date", narrow: true },
  { key: "coverage.groupNumber", label: "Group number", kind: "text", narrow: true },
  {
    key: "coverage.relationship",
    label: "Relationship to holder",
    kind: "select",
    narrow: true,
    options: [
      { value: "self", label: "Self" },
      { value: "spouse", label: "Spouse" },
      { value: "child", label: "Child" },
      { value: "other", label: "Other" },
    ],
  },
];

const ALL_SPECS = [
  ...IDENTITY_SPECS,
  ...CONTACT_SPECS,
  ...ADDRESS_SPECS,
  ...EMERGENCY_SPECS,
  COVERAGE_KIND_SPEC,
  ...COVERAGE_SPECS,
];

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A date box can hold a full date only; a partial imported expiry shows blank. */
function fullDate(value: string | undefined): string {
  return value !== undefined && ISO_DATE.test(value) ? value : "";
}

function initialForm(demographics: PatientDemographics): FormState {
  const address = demographics.address ?? {};
  const contact = demographics.emergencyContact ?? {};
  const coverage = demographics.coverage;
  return {
    legalFirstName: demographics.legalFirstName,
    legalMiddleName: demographics.legalMiddleName ?? "",
    legalLastName: demographics.legalLastName,
    preferredName: demographics.preferredName ?? "",
    dateOfBirth: demographics.dateOfBirth,
    administrativeSex: demographics.administrativeSex,
    phone: demographics.phone ?? "",
    email: demographics.email ?? "",
    "address.line1": text(address.line1),
    "address.line2": text(address.line2),
    "address.city": text(address.city),
    "address.region": text(address.region ?? address.province ?? address.state),
    "address.postalCode": text(address.postalCode),
    "address.country": text(address.country),
    "emergencyContact.name": text(contact.name),
    "emergencyContact.relationship": text(contact.relationship),
    "emergencyContact.phone": text(contact.phone),
    "coverage.kind": coverage ? "insurance" : "cash",
    "coverage.payerName": coverage?.payerName ?? "",
    "coverage.payerKind": coverage?.payerKind ?? "provincial_plan",
    "coverage.memberId": coverage?.memberId ?? "",
    "coverage.versionCode": coverage?.versionCode ?? "",
    "coverage.expiryDate": fullDate(coverage?.expiryDate),
    "coverage.groupNumber": coverage?.groupNumber ?? "",
    "coverage.relationship": coverage?.relationship ?? "self",
  };
}

function compactObject(
  form: FormState,
  prefix: "address" | "emergencyContact",
  keys: readonly string[],
): Record<string, string> | null {
  const output: Record<string, string> = {};
  for (const key of keys) {
    const value = form[`${prefix}.${key}`]?.trim() ?? "";
    if (value.length > 0) output[key] = value;
  }
  return Object.keys(output).length === 0 ? null : output;
}

const ADDRESS_KEYS = ["line1", "line2", "city", "region", "postalCode", "country"] as const;
const CONTACT_KEYS = ["name", "relationship", "phone"] as const;

const VERSION_CODE_KEY = "coverage.versionCode";
const EXPIRY_DATE_KEY = "coverage.expiryDate";
/** The health card details only a provincial plan shows. */
const CARD_KEYS: ReadonlySet<string> = new Set([VERSION_CODE_KEY, EXPIRY_DATE_KEY]);

function isProvincialPlan(form: FormState): boolean {
  return form["coverage.kind"] === "insurance" && form["coverage.payerKind"] === "provincial_plan";
}

function versionCode(form: FormState): string {
  return (form[VERSION_CODE_KEY] ?? "").trim().toUpperCase();
}

function expiryDate(form: FormState): string {
  return (form[EXPIRY_DATE_KEY] ?? "").trim();
}

/** The update payload: `expectedVersion` plus only what differs from the record. */
export function demographicsChanges(
  initial: PatientDemographics,
  form: FormState,
): PatientDemographicsUpdateInput {
  const base = initialForm(initial);
  const changes: Record<string, unknown> = { expectedVersion: initial.version };
  for (const key of [
    "legalFirstName",
    "legalMiddleName",
    "legalLastName",
    "preferredName",
    "phone",
    "email",
  ]) {
    const next = form[key]?.trim() ?? "";
    if (next !== (base[key] ?? "").trim()) changes[key] = next;
  }
  if ((form.dateOfBirth ?? "") !== base.dateOfBirth) changes.dateOfBirth = form.dateOfBirth;
  if ((form.administrativeSex ?? "") !== base.administrativeSex) {
    changes.administrativeSex = form.administrativeSex;
  }
  const address = compactObject(form, "address", ADDRESS_KEYS);
  if (JSON.stringify(address) !== JSON.stringify(compactObject(base, "address", ADDRESS_KEYS))) {
    changes.address = address;
  }
  const contact = compactObject(form, "emergencyContact", CONTACT_KEYS);
  if (
    JSON.stringify(contact) !==
    JSON.stringify(compactObject(base, "emergencyContact", CONTACT_KEYS))
  ) {
    changes.emergencyContact = contact;
  }
  const coverageKeys = [
    "kind",
    "payerName",
    "payerKind",
    "memberId",
    "groupNumber",
    "relationship",
  ];
  // Only a provincial plan has a version code or an expiry date; each travels
  // when the card has or had one, so an untouched empty box never sends a clear.
  const sendsVersionCode =
    isProvincialPlan(form) && (versionCode(form) !== "" || versionCode(base) !== "");
  const sendsExpiryDate =
    isProvincialPlan(form) && (expiryDate(form) !== "" || expiryDate(base) !== "");
  const coverageChanged =
    form["coverage.kind"] !== base["coverage.kind"] ||
    (form["coverage.kind"] === "insurance" &&
      coverageKeys.some(
        (key) => (form[`coverage.${key}`] ?? "").trim() !== (base[`coverage.${key}`] ?? "").trim(),
      )) ||
    (sendsVersionCode && versionCode(form) !== versionCode(base)) ||
    (sendsExpiryDate && expiryDate(form) !== expiryDate(base));
  if (coverageChanged) {
    changes.coverage =
      form["coverage.kind"] === "cash"
        ? { kind: "cash" }
        : {
            kind: "insurance",
            payerName: form["coverage.payerName"]?.trim() ?? "",
            payerKind: form["coverage.payerKind"] || "provincial_plan",
            memberId: form["coverage.memberId"]?.trim() ?? "",
            ...((form["coverage.groupNumber"] ?? "").trim().length > 0
              ? { groupNumber: form["coverage.groupNumber"]?.trim() }
              : {}),
            relationship: form["coverage.relationship"] || "self",
            ...(sendsVersionCode ? { versionCode: versionCode(form) } : {}),
            ...(sendsExpiryDate ? { expiryDate: expiryDate(form) } : {}),
          };
  }
  return changes as PatientDemographicsUpdateInput;
}

/** True when saving would put a new plan on file, not just correct the card's details. */
function replacesPlan(initial: PatientDemographics, form: FormState): boolean {
  const base = initialForm(initial);
  return (
    demographicsChanges(initial, {
      ...form,
      [VERSION_CODE_KEY]: base[VERSION_CODE_KEY] ?? "",
      [EXPIRY_DATE_KEY]: base[EXPIRY_DATE_KEY] ?? "",
    }).coverage !== undefined
  );
}

export function DemographicsEditor({
  demographics,
  resetKey,
  saving,
  serverError,
  onSave,
  onCancel,
  onReload,
  onDirtyChange,
}: {
  readonly demographics: PatientDemographics;
  readonly resetKey: string;
  readonly saving: boolean;
  readonly serverError: string | null;
  readonly onSave: (input: PatientDemographicsUpdateInput) => void;
  readonly onCancel: () => void;
  readonly onReload?: (() => void) | undefined;
  readonly onDirtyChange?: (dirty: boolean) => void;
}) {
  const [form, setForm] = useState<FormState>(() => initialForm(demographics));
  const [issues, setIssues] = useState<EditorIssue[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const idPrefix = `pp-demo-${resetKey.replace(/[^a-z0-9]+/gi, "-")}`;

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is the intentional trigger; a refetch must not wipe the draft.
  useLayoutEffect(() => {
    setForm(initialForm(demographics));
    setIssues([]);
    setFieldErrors({});
  }, [resetKey]);

  const changes = demographicsChanges(demographics, form);
  const dirty = Object.keys(changes).length > 1;
  useLayoutEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const update = (key: string, value: string | boolean) => {
    const text = typeof value === "string" ? value : "";
    setForm((current) => ({
      ...current,
      [key]: key === VERSION_CODE_KEY ? text.toUpperCase() : text,
    }));
    setFieldErrors((current) => {
      if (current[key] === undefined) return current;
      const next = { ...current };
      delete next[key];
      return next;
    });
  };

  const submit = () => {
    if (!dirty) {
      onCancel();
      return;
    }
    const parsed = PatientDemographicsUpdateInputSchema.safeParse(changes);
    if (!parsed.success) {
      const nextIssues: EditorIssue[] = [];
      const nextErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const path = issue.path.map(String);
        const key = path[0] === "coverage" ? `coverage.${path[1] ?? "kind"}` : path.join(".");
        const spec = ALL_SPECS.find((candidate) => candidate.key === key);
        nextErrors[key] = issue.message;
        nextIssues.push({
          key: `${key}:${issue.message}`,
          message: `${spec?.label ?? key}: ${issue.message}`,
          inputId: `${idPrefix}-${key}`,
        });
      }
      setIssues(nextIssues);
      setFieldErrors(nextErrors);
      return;
    }
    setIssues([]);
    setFieldErrors({});
    onSave(parsed.data);
  };

  const field = (spec: FieldSpec) => (
    <FieldInput
      key={spec.key}
      id={`${idPrefix}-${spec.key}`}
      spec={spec}
      value={form[spec.key] ?? ""}
      error={fieldErrors[spec.key]}
      onChange={(value) => update(spec.key, value)}
    />
  );

  return (
    <SectionEditorFrame
      label="Demographics"
      issues={issues}
      serverError={serverError}
      saving={saving}
      dirty={dirty}
      onSubmit={submit}
      onCancel={onCancel}
      onReload={onReload}
    >
      <div className="pp-editor-group">
        <p className="pp-subhead">Identity</p>
        <div className="pp-editor-fields">{IDENTITY_SPECS.map(field)}</div>
      </div>
      <div className="pp-editor-group">
        <p className="pp-subhead">Contact</p>
        <div className="pp-editor-fields">{CONTACT_SPECS.map(field)}</div>
      </div>
      <div className="pp-editor-group">
        <p className="pp-subhead">Address</p>
        <div className="pp-editor-fields">{ADDRESS_SPECS.map(field)}</div>
      </div>
      <div className="pp-editor-group">
        <p className="pp-subhead">Emergency contact</p>
        <div className="pp-editor-fields">{EMERGENCY_SPECS.map(field)}</div>
      </div>
      <div className="pp-editor-group">
        <p className="pp-subhead">Coverage</p>
        <div className="pp-editor-fields">
          {field(COVERAGE_KIND_SPEC)}
          {form["coverage.kind"] === "insurance"
            ? COVERAGE_SPECS.filter(
                (spec) => !CARD_KEYS.has(spec.key) || isProvincialPlan(form),
              ).map(field)
            : null}
        </div>
        {form["coverage.kind"] === "insurance" &&
        demographics.coverage !== undefined &&
        dirty &&
        replacesPlan(demographics, form) ? (
          <p className="pp-quiet">Saving replaces the current plan on file.</p>
        ) : null}
      </div>
    </SectionEditorFrame>
  );
}
